import { useMemo } from "react"
import { TiposExtras, useGetTicketForDetailQuery } from "src/gql/schema"
import Skeleton from "src/shared/components/layout/skeleton/Skeleton"
import { capitalize } from "src/shared/helpers/capitalize"
import DescriptionDetail from "src/shared/components/data-display/description-detail/DescriptionDetail"
import DescriptionRS from "src/pages/room-service/ordenes/drawer/description/Description"
import Drawer from "src/shared/components/layout/drawer/Drawer"
import { getExactDate } from "src/utils/date"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { getCurrencyFormat } from "src/utils/string"
import { getTax } from "src/shared/sections/payment/Payment.helpers"
import { add, sum } from "src/shared/helpers/calculator"
import { getPayments } from "./helpers/pago"
import "./DetalleMovimiento.css"

function DetalleMovimiento({
    onClose,
    onCancelar,
    onEditar,
    isOpen,
    ticketId,
    folioVenta,
    data: movimiento
}: {
    onClose: () => void
    onEditar: (movimiento: any) => void
    onCancelar: (movimiento: any) => void
    isOpen: boolean
    ticketId: string
    folioVenta?: string
    data: any
}): JSX.Element {
    const { data, loading } = useGetTicketForDetailQuery({
        variables: {
            ticket_id: ticketId
        },
    })
    const { formatCustomDate } = useFormatDate()
    const ticket_data = data?.ticket?.data
    const total = Number(movimiento?.total_movimiento || 0)
    const propinas = sum(ticket_data?.propinas?.map((i) => Number(i.total || 0)) || [])
    const habitacion = ticket_data?.habitaciones?.[0]
    const gasto = data?.ticket?.gasto

    const isPagoMixto = movimiento?.metodo_pago === "Mixto"
    const isRooomService = Number(ticket_data?.articulos?.length || 0) > 0
    const isPropina = movimiento?.tipo === "Propina"
    const isIngreso = !gasto && !isPropina
    const isOtros = ["Anticipo", "Reserva", "Saldo"].some(tipo => movimiento?.tipo?.includes(tipo))

    const origen_orden = data?.ticket?.ordenes?.[0]?.origen_orden || ""
    const habitacion_orden = data?.ticket?.ordenes?.[0]?.renta?.habitacion

    const getExtra = (tipo: string) => {
        let cantidad = 0
        let total = 0
        ticket_data?.extras.forEach((i) => {
            if (i.tipo === tipo) {
                cantidad = add(cantidad, Number(i?.cantidad || 0))
                total = add(total, Number(i?.costo || 0))
            }
        })

        return {
            cantidad,
            total
        }
    }

    const extras = useMemo(() => {
        return {
            personas: getExtra(TiposExtras.Persona),
            horas: getExtra(TiposExtras.Hora),
            hospedajes: getExtra(TiposExtras.Hospedaje),
        }
    }, [data?.ticket?.ticket_id])

    const articulos = useMemo(() => {
        const data_articulos = ticket_data?.articulos || []
        const articulos_ids = [...new Set(data_articulos.map((i) => i.articulo_id) || [])]
        return articulos_ids.map((articulo_id) => {
            let obj = {
                nombre: "",
                cantidad: 0,
                costo: 0,
                icon: ""
            }
            data_articulos?.forEach((i) => {
                if (i.articulo_id === articulo_id) {
                    obj = {
                        nombre: i.nombre,
                        icon: "",
                        cantidad: add(obj.cantidad, Number(i?.cantidad || 0)),
                        costo: add(obj.costo, Number(i?.costo || 0)),
                    }
                }
            })
            return obj
        })
    }, [data?.ticket?.ticket_id])

    const opciones = useMemo(() => {
        const editar = movimiento?.allowEditar ? [{
            label: "Editar método de pago",
            onClick: () => {
                onClose()
                onEditar(movimiento)
            }
        }] : []
        const cancelar = movimiento?.allowCancelar ? [{
            label: "Cancelar",
            onClick: () => {
                onClose()
                onCancelar(movimiento)
            }
        }] : []
        return [...editar, ...cancelar]
    }, [movimiento])

    return (
        <Drawer
            placement={"right"}
            bar={false}
            itemsMenu={opciones}
            visible={isOpen}
            withCloseButton={true}
            onClose={onClose}
            withMenu={opciones.length > 0}
        >
            <div>
                <div className="detalle-h-view__head" style={{ marginTop: 24 }}>
                    {!loading ? <p className="detalle-h-view__title">{movimiento?.folio || "-"}</p> : <Skeleton.Item drawer style={{ height: 29, width: 200 }} />}
                    {!loading ? <p className="detalle-h-view__subtitle">{movimiento?.categoria || "-"}</p> : <Skeleton.Item drawer style={{ height: 17, width: 150 }} />}
                    {!loading ? folioVenta ? <p className="detalle-h-view__subtitle">{`Ticket: ${folioVenta}`}</p> : null : <Skeleton.Item drawer style={{ height: 17, width: 150 }} />}
                </div>
            </div>
            <div className="d-movimiento__body">
                <div
                    className="d-movimiento__description"
                    style={{ marginBottom: 15 }}
                >
                    {!loading ? (
                        <>
                            <DescriptionDetail
                                icon={"calendarChecked"}
                                label={`Fecha y hora${isIngreso ? " de venta" : ""}`}
                                value={
                                    data?.ticket?.fecha_impresion
                                        ? formatCustomDate(getExactDate(data?.ticket?.fecha_impresion), "DD/MMM/YY hh:mm a")
                                        : "-"
                                }
                            />
                            <DescriptionDetail
                                className={"d-movimiento__responsable"}
                                icon={"userFilled"}
                                label={"Responsable"}
                                value={movimiento?.responsable || "-"}
                            />
                            {isRooomService ? <DescriptionDetail
                                icon={"ArticleFill"}
                                label={"Origen"}
                                value={habitacion_orden ? `${habitacion_orden?.tipo_habitacion?.nombre || ""} ${habitacion_orden?.numero_habitacion || ""}` : capitalize(origen_orden || "")}
                            /> : null}
                        </>
                    ) : (
                        <>
                            <Skeleton.Item drawer style={{ height: 40, width: 200 }} />
                            <Skeleton.Item drawer style={{ height: 40, width: 200 }} />
                            <Skeleton.Item drawer style={{ height: 40, width: 200 }} />
                        </>
                    )}
                </div>
                <div className="d-movimiento__line" />
                {!loading ? <p className="d-movimiento__subtitle">{isOtros || isPropina ? "Detalle del movimiento" : gasto ? "Detalle del gasto" : isRooomService ? "Productos de la orden" : "Detalle de venta"}</p> : <Skeleton.Item drawer style={{ height: 24, width: 200, marginBottom: 20 }} />}
                {!loading ? isOtros ? (
                    <div
                        className="d-movimiento__description"
                        style={{ marginBottom: 15 }}
                    >
                        <DescriptionDetail
                            icon={"dollarCircle"}
                            label={"Monto"}
                            value={getCurrencyFormat(total)}
                        />
                    </div>
                )
                : gasto ? (
                    <div
                        className="d-movimiento__description"
                        style={{ marginBottom: 15 }}
                    >
                        {gasto.categoria ? (
                            <DescriptionDetail
                                className={"d-movimiento__responsable"}
                                icon={"FolderFill"}
                                label={"Categoría"}
                                value={gasto.categoria}
                            />
                        ) : null}
                        {gasto.concepto ? (
                            <DescriptionDetail
                                className={"d-movimiento__responsable"}
                                icon={"Document"}
                                label={"Concepto"}
                                value={gasto.concepto || "-"}
                            />
                        ) : null}
                        <DescriptionDetail
                            icon={"dollarCircle"}
                            label={"Monto"}
                            value={getCurrencyFormat(total)}
                        />
                    </div>
                ) : isRooomService ? (
                    <div
                        className="d-movimiento__description"
                        style={{ marginBottom: 15 }}
                    >
                        {articulos.map(({ icon = "", nombre = "", costo = 0, cantidad = 0 }, index) => (
                            <DescriptionRS
                                icon={icon}
                                label1={nombre}
                                value2={getCurrencyFormat(costo)}
                                value1={`${cantidad}`}
                                key={index}
                            />
                        ))}
                    </div>
                ) : !isPropina ? (
                    <div
                        className="d-movimiento__description"
                        style={{ marginBottom: 15 }}
                    >
                        {habitacion ? <DescriptionDetail
                            icon={"HotelFilled"}
                            label={"Estancia"}
                            value={habitacion.tipo}
                            amount={Number(habitacion.costo || 0)}
                            currencyFormat={"simple"}
                        /> : null}
                        {extras.personas.cantidad > 0 ? (
                            <DescriptionDetail
                                icon={"UserParentFill"}
                                label={"Personas extras"}
                                value={`${extras.personas.cantidad}`}
                                amount={Number(extras.personas.total || 0)}
                                currencyFormat={"simple"}
                            />
                        ) : null}
                        {extras.hospedajes.cantidad > 0 ? (
                            <DescriptionDetail
                                icon={"MoonFill"}
                                label={"Noches extra"}
                                value={`${extras.hospedajes.cantidad}`}
                                amount={Number(extras.hospedajes.total || 0)}
                                currencyFormat={"simple"}
                            />
                        ) : null}
                        {extras.horas.total ? (
                            <DescriptionDetail
                                icon={"Clock"}
                                label={"Horas extra"}
                                value={`${extras.horas.cantidad}`}
                                amount={Number(extras.horas.total || 0)}
                                currencyFormat={"simple"}
                            />
                        ) : null}
                    </div>
                ) : null : (
                    <div
                        className="d-movimiento__description"
                        style={{ marginBottom: 15 }}
                    >
                        <Skeleton.Item drawer style={{ height: 40, width: 200 }} />
                        <Skeleton.Item drawer style={{ height: 40, width: 200 }} />
                        <Skeleton.Item drawer style={{ height: 40, width: 200 }} />
                    </div>
                )}
                {!loading && propinas > 0 ? (
                    <DescriptionDetail
                        icon={"MoneyDollarBox"}
                        label={"Propina"}
                        value={getCurrencyFormat(Number(propinas || 0))}
                        style={{ marginBottom: 20 }}
                    />
                ) : null}
                <div className="d-movimiento__line" />
                {!loading ? <DescriptionDetail
                    className={"d-movimiento__pago"}
                    icon={"creditCard"}
                    label={"Método de pago"}
                    value={isPagoMixto ? `Mixto\n${getPayments(movimiento?.pago_origen || "")}` : `${movimiento?.metodo_pago || ""} ${movimiento?.pago_origen?.split(" / ")?.[1] || ""}`}
                /> : <Skeleton.Item drawer style={{ height: 40, width: 200 }} />}
            </div>
            <div className="d-movimiento__footer">
                <div className="d-movimiento__line" />
                {!loading ? isIngreso ? <p className="d-movimiento__subtotal">
                    <span>{"Impuestos"}</span>
                    <span>{getCurrencyFormat(getTax(total))}</span>
                </p> : null : <Skeleton.Item drawer style={{ height: 21, width: "100%", marginBottom: 10 }} />}
                {!loading ? <p className="d-movimiento__total">
                    <span>{`Total${isIngreso ? " pagado" : ""}`}</span>
                    <span>{getCurrencyFormat(total)}</span>
                </p> : <Skeleton.Item drawer style={{ height: 30, width: "100%" }} />}
            </div>
        </Drawer>
    )
}

export default DetalleMovimiento